/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { ajax } from 'rxjs/ajax';
import { concat, of } from 'rxjs';
import { catchError, filter, mergeMap, switchMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import keyBy from 'lodash/keyBy';

import { SecurityHelper as SecurityUtils, STATUS } from 'sarsaparilla';

import { getToursByFacilityUrl } from '../api/endpoints';
import {
    FETCH_TOURS_BY_FACILITY,
    fetchToursByFacilityError,
    fetchToursByFacilitySuccess,
    setFetchToursByFacilityStatus,
} from '../actions/toursDataActions';

import { fetchTourImagesByFacility } from '../actions/tourImagesActions';
import { fetchTourRatingsByFacility } from '../actions/tourRatingsActions';
import { getAccountTypeSelector } from '../utils';

/**
 * Listen for the FETCH_TOURS_BY_FACILITY action to be dispatched
 * then get data for all the individual tours that belong to the passed facilityId.
 *
 * Then dispatch actions to
 *   - fetch the images for each tour
 *   - fetch rating-review summary for each tour
 */
export function fetchToursByFacility(actions$) {
    return actions$.pipe(
        ofType(FETCH_TOURS_BY_FACILITY),
        filter(({ payload }) => !!payload),
        switchMap(({ payload: facilityId }) => {
            let isCommercialAccount = SecurityUtils.isCommercialAccount();
            const baseUrl = getToursByFacilityUrl(facilityId);
            if (isCommercialAccount) {
                const account = SecurityUtils.getAccount();
                const accountType = getAccountTypeSelector(account, facilityId);
                if (accountType !== 'COMM') {
                    isCommercialAccount = false;
                }
            }
            return concat(
                of(setFetchToursByFacilityStatus(STATUS.PENDING)),
                ajax.getJSON(`${baseUrl}&filterCommTours=${!isCommercialAccount}`).pipe(
                    mergeMap((response) => {
                        const tourList = response;
                        const tourMap = keyBy(response, 'tour_id');
                        const tourIds = response.map((item) => item.tour_id);

                        const sortedTourList = tourList.sort((a, b) => {
                            if (a.tour_name > b.tour_name) {
                                return 1;
                            }
                            if (a.tour_name < b.tour_name) {
                                return -1;
                            }
                            return 0;
                        });

                        return [
                            fetchToursByFacilitySuccess({
                                tourList: sortedTourList,
                                tourMap,
                            }),
                            fetchTourImagesByFacility({ facilityId, tourIds }),
                            fetchTourRatingsByFacility({ facilityId, tourIds }),
                        ];
                    }),
                    catchError((error) => of(fetchToursByFacilityError(error)))
                )
            );
        })
    );
}
